import { Lightning, Storage } from "@lightningjs/sdk";
import { SettingsItemText } from "../layout/SettingsItemText";
import { SettingsPinContent } from "../layout/SettingsPinContent";
import { SettingsAgeContent } from "../layout/SettingsAgeContent";
import { STORAGE_KEYS } from "../../../../../lib/utils";
import { isPostTvLinux, isPostTvAndroid } from "../../../../../lib/utils";
import { postTvWebSocket } from "../../../../../lib/websocket";
import { updateParentalPin } from "../../../../../api/mutations";
import { refreshUserData } from "../../../../../lib/utils";

interface SettingsAgeRestrictionTemplateSpec extends Lightning.Component.TemplateSpec {
    Layout: typeof SettingsItemText;
}

export class SettingsAgeRestriction
    extends Lightning.Component<SettingsAgeRestrictionTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsAgeRestrictionTemplateSpec>
{
    private _showingAgeContent = false;

    readonly SettingsItem = this.getByRef("Layout")!;

    static override _template(): Lightning.Component.Template<SettingsAgeRestrictionTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            Layout: {
                type: SettingsItemText,
                title: "settings.ageRestriction",
                content: SettingsPinContent
            }
        };
    }

    override _getFocused() {
        return this.SettingsItem;
    }

    override _focus() {
        // Reset to PIN content when re-focused from the list
        this._showPinContent();
        const pinContent = this._getContent();
        if (pinContent && !this._showingAgeContent) {
            if (this._hasPin()) {
                pinContent.open(undefined, undefined, "verify");
            } else {
                pinContent.open(undefined, undefined, "set");
            }
        }
    }

    override _captureKey(e: KeyboardEvent) {
        if (!(this.SettingsItem as any)._focusList) {
            if (e.key === "Enter" || e.keyCode === 13 || e.key === "ArrowRight") {
                this._openAgeInput();
                return true;
            }
        }
        return false;
    }

    _handleHover() {
        this.fireAncestors("$listItemHover", this);
    }

    _hasPin(): boolean {
        const user = Storage.get(STORAGE_KEYS.user) as any;
        return !!(user?.settings?.parentalPin) || !!Storage.get(STORAGE_KEYS.parentalPin);
    }

    _getContent(): any {
        return (this.SettingsItem as any).CustomContent?.children?.[0];
    }

    _swapToAgeContent() {
        const customContent = (this.SettingsItem as any).CustomContent;
        if (!customContent) return;
        customContent.children = [{ type: SettingsAgeContent }];
        this._showingAgeContent = true;
    }

    _showPinContent() {
        if (!this._showingAgeContent) return;
        const customContent = (this.SettingsItem as any).CustomContent;
        if (!customContent) return;
        customContent.children = [{ type: SettingsPinContent }];
        this._showingAgeContent = false;
    }

    $unfocusContent() {
        // Reset to PIN content when leaving the right panel
        this._showPinContent();
        (this.SettingsItem as any).unfocusContent();
    }

    _openAgeInput() {
        // Ensure we start with PIN content
        this._showPinContent();
        const pinContent = this._getContent();
        if (!pinContent) return;

        if (this._hasPin()) {
            // PIN exists: verify first, then show age buttons
            pinContent.open(
                () => {
                    this._showAgeButtons();
                },
                undefined,
                "verify"
            );
        } else {
            // No PIN: set PIN first, then show age buttons
            pinContent.open(
                () => {},
                undefined,
                "set",
                (pin: string) => {
                    this._savePin(pin);
                    setTimeout(() => {
                        this._showAgeButtons();
                    }, 100);
                }
            );
        }

        (this.SettingsItem as any).focusContent();
    }

    _showAgeButtons() {
        setTimeout(() => {
            this._swapToAgeContent();

            // Wait for component to initialize
            setTimeout(() => {
                const ageContent = this._getContent();
                if (ageContent && typeof ageContent.open === "function") {
                    ageContent.open((age: string) => {
                        this._saveAgeRestriction(age);
                    });
                }
            }, 50);
        }, 100);
    }

    async _savePin(pin: string) {
        try {
            await updateParentalPin(pin);
            await refreshUserData();
            Storage.set(STORAGE_KEYS.parentalPin, pin);

            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.pin", pin, () => {});
                } catch (e) {
                    console.error("[SettingsAgeRestriction] Error saving PIN via WebSocket:", e);
                }
            }

            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore
                    if (android && typeof android.setPinCode === "function") {
                        // @ts-ignore
                        android.setPinCode(pin);
                    }
                } catch (e) {
                    console.error("[SettingsAgeRestriction] Error saving PIN via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsAgeRestriction] Error saving PIN via API:", error);
            Storage.set(STORAGE_KEYS.parentalPin, pin);
        }
    }

    async _saveAgeRestriction(age: string) {
        try {
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const currentPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);

            if (currentPin) {
                await updateParentalPin(currentPin, age);
                await refreshUserData();
            }

            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);

            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try {
                    postTvWebSocket.setSettings("pics.age", age, () => {});
                } catch (e) {
                    console.error("[SettingsAgeRestriction] Error saving age via WebSocket:", e);
                }
            }

            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore
                    if (android && typeof android.setAgeRestriction === "function") {
                        // @ts-ignore
                        android.setAgeRestriction(parseInt(age, 10));
                    }
                } catch (e) {
                    console.error("[SettingsAgeRestriction] Error saving age via Android API:", e);
                }
            }
        } catch (error) {
            console.error("[SettingsAgeRestriction] Error saving age via API:", error);
            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);
        }
    }
}
